/**
 * Request detail — single-record view.
 *
 * Shows: header (code + type + status), workflow timeline, payload data,
 * action history, and the action buttons (approve / reject / cancel / fulfill)
 * gated by what the server says the current viewer can do.
 *
 * Server response includes ``current_approver`` (Person FK) — we infer
 * "can I approve?" by comparing it to the current user's person_id (from
 * /auth/me/). If the user is admin, action buttons always show.
 */

import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Check, X, Send } from 'lucide-react'
import { api } from '../lib/api'
import AdminLayout from '../components/AdminLayout'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { extractErr } from '@/components/ui/inline-edit'

// ─────────────────── Types ───────────────────

type SchemaField = {
  name: string
  label: string
  type: string
  required?: boolean
  options?: string[]
}

type WorkflowStep = {
  id: number
  order: number
  name: string
  approver_resolver: string
  approver_role_name: string | null
  approver_person_name: string | null
}

type ApprovalAction = {
  id: number
  step: number | null
  step_name: string | null
  action: 'SUBMIT' | 'APPROVE' | 'REJECT' | 'CANCEL' | 'COMMENT' | 'FULFILL'
  actor: number | null
  actor_name: string | null
  comment: string
  timestamp: string
}

type Fulfillment = {
  id: number
  assigned_role: number | null
  assigned_role_name: string | null
  assigned_person: number | null
  assigned_person_name: string | null
  status: 'PENDING' | 'DONE'
  notes: string
  fulfilled_by: number | null
  fulfilled_by_name: string | null
  fulfilled_at: string | null
}

type RequestDetail = {
  id: number
  code: string
  request_type: number
  request_type_code: string
  request_type_name: string
  request_type_icon: string
  request_type_schema: SchemaField[]
  workflow: number
  workflow_code: string
  workflow_steps: WorkflowStep[]
  requester: number
  requester_name: string
  summary: string
  data: Record<string, any>
  status: 'DRAFT' | 'PENDING' | 'APPROVED' | 'REJECTED' | 'CANCELLED' | 'FULFILLED'
  current_step: number | null
  current_step_name: string | null
  current_approver: number | null
  current_approver_name: string | null
  submitted_at: string | null
  resolved_at: string | null
  actions: ApprovalAction[]
  fulfillment: Fulfillment | null
  created_at: string
  updated_at: string
}

type Me = {
  id: number
  is_staff: boolean
  is_superuser: boolean
  person_id: number | null
  roles: { id: number; name: string }[]
}

// ─────────────────── Constants ───────────────────

const STATUS_COLOR: Record<RequestDetail['status'], string> = {
  DRAFT: 'bg-gray-100 text-gray-700',
  PENDING: 'bg-amber-100 text-amber-800',
  APPROVED: 'bg-blue-100 text-blue-800',
  REJECTED: 'bg-red-100 text-red-700',
  CANCELLED: 'bg-gray-100 text-gray-600',
  FULFILLED: 'bg-green-100 text-green-800',
}

const ACTION_COLOR: Record<ApprovalAction['action'], string> = {
  SUBMIT: 'bg-slate-100 text-slate-700',
  APPROVE: 'bg-green-100 text-green-800',
  REJECT: 'bg-red-100 text-red-700',
  CANCEL: 'bg-gray-100 text-gray-600',
  COMMENT: 'bg-blue-100 text-blue-800',
  FULFILL: 'bg-emerald-100 text-emerald-800',
}

// ─────────────────── Page ───────────────────

export default function RequestDetail() {
  const { id } = useParams<{ id: string }>()
  const reqId = id ? Number(id) : 0
  const navigate = useNavigate()
  const qc = useQueryClient()

  const reqQ = useQuery({
    queryKey: ['requests', 'detail', reqId],
    queryFn: async () => (await api.get<RequestDetail>(`/requests/${reqId}/`)).data,
    enabled: !!reqId,
  })
  const meQ = useQuery({
    queryKey: ['me'],
    queryFn: async () => (await api.get<Me>('/auth/me/')).data,
    staleTime: 5 * 60 * 1000,
  })

  const [comment, setComment] = useState('')

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['requests', 'detail', reqId] })
    qc.invalidateQueries({ queryKey: ['requests', 'mine'] })
    qc.invalidateQueries({ queryKey: ['requests', 'inbox'] })
    qc.invalidateQueries({ queryKey: ['requests', 'inbox-counts'] })
  }

  const approveMut = useMutation({
    mutationFn: async () =>
      (await api.post(`/requests/${reqId}/approve/`, { comment })).data,
    onSuccess: () => { toast.success('Approved'); setComment(''); invalidate() },
    onError: (e) => toast.error(extractErr(e)),
  })
  const rejectMut = useMutation({
    mutationFn: async () =>
      (await api.post(`/requests/${reqId}/reject/`, { comment })).data,
    onSuccess: () => { toast.success('Rejected'); setComment(''); invalidate() },
    onError: (e) => toast.error(extractErr(e)),
  })
  const cancelMut = useMutation({
    mutationFn: async () =>
      (await api.post(`/requests/${reqId}/cancel/`, { comment })).data,
    onSuccess: () => { toast.success('Request cancelled'); setComment(''); invalidate() },
    onError: (e) => toast.error(extractErr(e)),
  })
  const fulfillMut = useMutation({
    mutationFn: async () =>
      (await api.post(`/requests/${reqId}/fulfill/`, { comment })).data,
    onSuccess: () => { toast.success('Marked fulfilled'); setComment(''); invalidate() },
    onError: (e) => toast.error(extractErr(e)),
  })

  if (reqQ.isLoading) {
    return <AdminLayout><p className="text-muted-foreground">Loading…</p></AdminLayout>
  }
  if (reqQ.isError || !reqQ.data) {
    return (
      <AdminLayout>
        <div className="bg-card rounded-lg shadow p-8 text-center">
          <p className="text-foreground mb-4">Request not found.</p>
          <Link to="/admin/requests" className="text-violet-600 hover:underline">← Back to My Requests</Link>
        </div>
      </AdminLayout>
    )
  }

  const r = reqQ.data
  const me = meQ.data

  const isAdmin = !!(me?.is_staff || me?.is_superuser)
  const isRequester = me?.person_id != null && me.person_id === r.requester
  const isCurrentApprover = me?.person_id != null && me.person_id === r.current_approver
  const userRoleIds = new Set((me?.roles || []).map((x) => x.id))
  const canFulfill =
    r.status === 'APPROVED' &&
    r.fulfillment &&
    r.fulfillment.status === 'PENDING' &&
    (isAdmin
      || (me?.person_id != null && r.fulfillment.assigned_person === me.person_id)
      || (r.fulfillment.assigned_role !== null && userRoleIds.has(r.fulfillment.assigned_role)))

  const canApprove = (isAdmin || isCurrentApprover) && r.status === 'PENDING'
  const canCancel = (isAdmin || isRequester) && (r.status === 'PENDING' || r.status === 'DRAFT')

  return (
    <AdminLayout>
      {/* Header */}
      <div className="mb-6">
        <div className="text-xs">
          <Link to="/admin/requests" className="text-muted-foreground hover:text-foreground hover:underline">
            ← My Requests
          </Link>
        </div>
        <div className="flex items-start justify-between mt-2 gap-4">
          <div className="flex-1 min-w-0">
            <div className="font-mono text-xs text-muted-foreground">{r.code}</div>
            <h2 className="text-2xl font-bold text-foreground">
              <span className="mr-2">{r.request_type_icon}</span>
              {r.summary}
            </h2>
            <div className="text-sm text-muted-foreground mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-1">
              <span>{r.request_type_name}</span>
              <span>·</span>
              <span>by {r.requester_name}</span>
              {r.submitted_at && (
                <>
                  <span>·</span>
                  <span>submitted {new Date(r.submitted_at).toLocaleString()}</span>
                </>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <span className={`text-xs px-2 py-0.5 rounded font-semibold ${STATUS_COLOR[r.status]}`}>
              {r.status}
            </span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: payload + actions */}
        <div className="lg:col-span-2 space-y-6">
          {/* Payload */}
          <section className="bg-card rounded-lg shadow p-6">
            <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-4">
              Request details
            </h3>
            <dl className="grid grid-cols-[180px_1fr] gap-x-6 gap-y-3 text-sm">
              {(r.request_type_schema || []).map((f) => (
                <Row key={f.name} label={f.label}>
                  <ValueView value={r.data?.[f.name]} fieldType={f.type} />
                </Row>
              ))}
              {(!r.request_type_schema || r.request_type_schema.length === 0) && (
                <p className="text-muted-foreground col-span-2 text-xs">No structured fields on this type.</p>
              )}
            </dl>
          </section>

          {/* Actions */}
          {(canApprove || canCancel || canFulfill) && (
            <section className="bg-card rounded-lg shadow p-6">
              <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-4">
                Take action
              </h3>
              <Textarea
                placeholder="Optional comment (visible in the audit trail)."
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                rows={3}
              />
              <div className="flex flex-wrap items-center gap-2 mt-3">
                {canApprove && (
                  <Button
                    onClick={() => approveMut.mutate()}
                    disabled={approveMut.isPending}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    <Check className="h-3 w-3 mr-1" /> Approve
                  </Button>
                )}
                {canApprove && (
                  <Button
                    variant="outline"
                    onClick={() => rejectMut.mutate()}
                    disabled={rejectMut.isPending}
                  >
                    <X className="h-3 w-3 mr-1" /> Reject
                  </Button>
                )}
                {canFulfill && (
                  <Button
                    onClick={() => fulfillMut.mutate()}
                    disabled={fulfillMut.isPending}
                  >
                    <Send className="h-3 w-3 mr-1" /> Mark fulfilled
                  </Button>
                )}
                {canCancel && (
                  <Button
                    variant="ghost"
                    onClick={() => cancelMut.mutate()}
                    disabled={cancelMut.isPending}
                  >
                    Cancel request
                  </Button>
                )}
              </div>
            </section>
          )}

          {/* Activity */}
          <section className="bg-card rounded-lg shadow p-6">
            <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-4">
              Activity ({r.actions.length})
            </h3>
            {r.actions.length === 0 ? (
              <p className="text-muted-foreground text-sm">No activity yet.</p>
            ) : (
              <ul className="divide-y divide-border">
                {r.actions.map((a) => (
                  <li key={a.id} className="py-3 first:pt-0 last:pb-0">
                    <div className="flex items-start gap-3">
                      <span className={`text-xs px-2 py-0.5 rounded font-semibold whitespace-nowrap ${ACTION_COLOR[a.action]}`}>
                        {a.action}
                      </span>
                      <div className="flex-1 min-w-0">
                        {a.step_name && (
                          <div className="text-xs text-muted-foreground mb-0.5">on {a.step_name}</div>
                        )}
                        {a.comment && <div className="text-sm whitespace-pre-wrap">{a.comment}</div>}
                        <div className="text-xs text-muted-foreground mt-0.5">
                          {a.actor_name || 'system'} · {new Date(a.timestamp).toLocaleString()}
                        </div>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>

        {/* Right: workflow + fulfillment */}
        <div className="space-y-6">
          <section className="bg-card rounded-lg shadow p-6">
            <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-4">
              Workflow — {r.workflow_code}
            </h3>
            <ol className="space-y-3">
              {r.workflow_steps.map((s) => {
                const isCurrent = s.id === r.current_step
                const isDone = r.actions.some((a) => a.step === s.id && a.action === 'APPROVE')
                const isRejected = r.actions.some((a) => a.step === s.id && a.action === 'REJECT')
                return (
                  <li key={s.id} className="flex items-start gap-3">
                    <div className={`mt-0.5 h-6 w-6 rounded-full text-xs font-semibold flex items-center justify-center shrink-0 ${
                      isDone ? 'bg-green-100 text-green-800'
                      : isRejected ? 'bg-red-100 text-red-700'
                      : isCurrent ? 'bg-amber-100 text-amber-800'
                      : 'bg-gray-100 text-gray-500'
                    }`}>
                      {s.order}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium">{s.name}</div>
                      <div className="text-xs text-muted-foreground">
                        {resolverLabel(s)}
                      </div>
                      {isCurrent && r.current_approver_name && (
                        <div className="text-xs text-amber-700 mt-0.5">
                          Waiting on {r.current_approver_name}
                        </div>
                      )}
                    </div>
                  </li>
                )
              })}
            </ol>
          </section>

          {r.fulfillment && (
            <section className="bg-card rounded-lg shadow p-6">
              <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-4">
                Fulfillment
              </h3>
              <dl className="grid grid-cols-[120px_1fr] gap-x-4 gap-y-2 text-sm">
                <dt className="text-xs font-semibold text-muted-foreground uppercase tracking-wide pt-0.5">Status</dt>
                <dd>
                  <span className={`text-xs px-2 py-0.5 rounded font-semibold ${r.fulfillment.status === 'DONE' ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}`}>
                    {r.fulfillment.status}
                  </span>
                </dd>
                <dt className="text-xs font-semibold text-muted-foreground uppercase tracking-wide pt-0.5">Assigned</dt>
                <dd className="text-sm">
                  {r.fulfillment.assigned_person_name
                    || r.fulfillment.assigned_role_name
                    || <span className="text-muted-foreground">—</span>}
                </dd>
                {r.fulfillment.fulfilled_by_name && (
                  <>
                    <dt className="text-xs font-semibold text-muted-foreground uppercase tracking-wide pt-0.5">By</dt>
                    <dd className="text-sm">
                      {r.fulfillment.fulfilled_by_name}
                      {r.fulfillment.fulfilled_at && (
                        <span className="text-muted-foreground ml-1">
                          · {new Date(r.fulfillment.fulfilled_at).toLocaleString()}
                        </span>
                      )}
                    </dd>
                  </>
                )}
                {r.fulfillment.notes && (
                  <>
                    <dt className="text-xs font-semibold text-muted-foreground uppercase tracking-wide pt-0.5">Notes</dt>
                    <dd className="text-sm whitespace-pre-wrap">{r.fulfillment.notes}</dd>
                  </>
                )}
              </dl>
            </section>
          )}
        </div>
      </div>
    </AdminLayout>
  )
}

// ─────────────────── Helpers ───────────────────

function resolverLabel(s: WorkflowStep): string {
  switch (s.approver_resolver) {
    case 'MANAGER': return 'Requester\'s direct manager'
    case 'MANAGER_OF_MANAGER': return 'Skip-level manager'
    case 'DEPARTMENT_HEAD': return 'Department head'
    case 'ROLE': return `Role: ${s.approver_role_name || '—'}`
    case 'PERSON': return `Person: ${s.approver_person_name || '—'}`
    default: return s.approver_resolver
  }
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <>
      <dt className="text-xs font-semibold text-muted-foreground uppercase tracking-wide pt-0.5">{label}</dt>
      <dd>{children}</dd>
    </>
  )
}

function ValueView({ value, fieldType }: { value: any; fieldType: string }) {
  if (value === null || value === undefined || value === '') {
    return <span className="text-muted-foreground">—</span>
  }
  if (fieldType === 'textarea') {
    return <span className="whitespace-pre-wrap">{String(value)}</span>
  }
  return <span>{String(value)}</span>
}
